/**
 * The small pixel face's advances for the shared rules that lay out names (ADR-061's note of 2026-10-16): the minimap's
 * Nearby names and the world map's names are placed on the server-free layout rules in this package, which may not read
 * the client's bitmap font, so the advances of printable ASCII are held here as plain data. `tests/small-text-width.test.ts`
 * holds the table to the advances `bun run ui:build` writes into `pixel-small.fnt`, so the two cannot drift.
 */

/**
 * The advance of each printable ASCII code point from the space (U+0020) to the tilde (U+007E), in logical pixels: the
 * glyph's width and one clear column.
 */
export const SMALL_ADVANCES: readonly number[] = [
  3, 2, 4, 5, 5, 5, 5, 2, 3, 3, 5, 4, 3, 4, 2, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 2, 2, 4, 5, 4, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 4, 5, 5, 5, 6, 5, 5, 5, 5, 5, 5, 5, 5, 5, 6, 5, 5, 5, 3, 5, 3, 4, 5, 3, 5, 5, 5, 5, 5, 5, 5, 5, 2, 4, 5, 4, 6, 5, 5, 5, 5, 5, 5, 5, 5, 5, 6, 5, 5, 5, 4, 2, 4, 5,
];

/**
 * What a character the table lacks is estimated at: the widest advance, so a box is never short.
 */
export const SMALL_ADVANCE_WIDEST = 6;

/**
 * The width of a text in the small pixel face at 1x: the sum of its characters' advances, as `pixelTextWidth(text,
 * "small")` measures it in the client.
 *
 * @param text The text.
 * @returns Logical pixels; a character outside printable ASCII counts `SMALL_ADVANCE_WIDEST`.
 * @example
 * smallTextWidth("Rain");
 * // 20
 */
export function smallTextWidth(text: string): number {
  let width = 0;
  for (let i = 0; i < text.length; i += 1) {
    width += SMALL_ADVANCES[text.charCodeAt(i) - 0x20] ?? SMALL_ADVANCE_WIDEST;
  }
  return width;
}

/**
 * The width of a name's box: its text without the clear column after the last glyph, and the one pixel outline on each
 * side of the ink.
 *
 * @param text The text.
 * @returns Logical pixels, 0 for no text.
 * @example
 * smallTextBoxWidth("Rain");
 * // 21
 */
export function smallTextBoxWidth(text: string): number {
  return text.length === 0 ? 0 : smallTextWidth(text) + 1;
}
